import { useCallback, useRef, MutableRefObject } from 'react';
import { CellPositionCord, CellPositionsConfig } from '../table-types';
import { keyPressHandler, isCopy } from './utils';

export const useEventListenersOnTableBody = ({
  currentCellPositionRef,
  cellPositionsConfig,
  isFocusable,
  tableId,
  rows,
}: {
  currentCellPositionRef: MutableRefObject<CellPositionCord>;
  cellPositionsConfig: CellPositionsConfig;
  isFocusable?: boolean;
  tableId: string;
  rows: any[];
}) => {
  const bodyRef = useRef<HTMLDivElement | null>(null);
  // Use refs for values that change frequently to keep the callback ref stable
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const isFocusableRef = useRef(isFocusable);
  isFocusableRef.current = isFocusable;
  const cellPositionsConfigRef = useRef(cellPositionsConfig);
  cellPositionsConfigRef.current = cellPositionsConfig;

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      let keyPressed = e.key;
      if (isCopy(e)) keyPressed = 'Copy';
      const handler = keyPressHandler(keyPressed, e);
      if (handler) {
        handler({
          currentCellPositionRef,
          cellPositionsConfig: cellPositionsConfigRef.current,
          tableId,
          rows: rowsRef.current,
        });
      }

      if (bodyRef?.current && currentCellPositionRef.current) {
        const { x, y } = currentCellPositionRef.current;
        const cellElem = document.querySelector(`[table-cell-position="${tableId}-${x}-${y}"]`) as HTMLElement;

        const bodyElem = document.querySelector(`#${tableId}`) as Element;
        const stickyColumns = document.querySelectorAll<HTMLElement>(
          `#${tableId} [role="columnheader"][data-sticky-td="true"]`
        );

        const stickyElem = stickyColumns
          ? Array.from(stickyColumns).reduce((acc, elem) => acc + elem.clientWidth, 0)
          : 0;

        const toScrollTo =
          cellElem.getBoundingClientRect().left +
          bodyElem.scrollLeft -
          bodyElem.getBoundingClientRect().left -
          stickyElem;

        bodyElem.scrollTo({
          left: toScrollTo,
          behavior: 'smooth',
        });
      }
    },
    [currentCellPositionRef, tableId]
  );

  const bodyWithListenersRefCb: React.RefCallback<HTMLDivElement> = useCallback(
    (bodyNode: HTMLDivElement | null) => {
      if (bodyRef.current) {
        bodyRef.current.removeEventListener('keydown', handleKeyDown);
      }

      if (bodyNode) {
        if (isFocusableRef.current) {
          bodyNode.addEventListener('keydown', handleKeyDown);
        }
      }

      bodyRef.current = bodyNode;
    },
    [handleKeyDown]
  );

  return bodyWithListenersRefCb;
};
